import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  X, 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin, 
  Building, 
  Edit, 
  Settings, 
  Key,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Receipt
} from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { asaasHelpers } from '../../lib/asaasHelpers';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const PersonDetailsModal = ({ 
  person, 
  isOpen, 
  onClose, 
  onPersonUpdate,  getPersonTypeIcon,
  getPersonTypeLabel,
  formatPhone,
  formatDocument 
}) => {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [syncingAsaas, setSyncingAsaas] = useState(false);

  if (!isOpen || !person) return null;
  const handleViewPurchaseHistory = () => {
    // Fechar o modal e navegar para histórico de vendas
    toast.success(`Redirecionando para histórico de compras de ${person.name}...`);
    onClose();
    navigate(`/dashboard/sales-history?person_id=${person.id}&person_name=${encodeURIComponent(person.name)}`);
  };

  const handleSyncAsaas = async () => {
    if (!company?.id) {
      toast.error('Empresa não encontrada');
      return;
    }

    try {
      setSyncingAsaas(true);
      
      const syncResult = await asaasHelpers.syncCustomer(person.id, company.id, true);
      
      if (syncResult.success) {
        if (syncResult.already_synced) {
          toast.success('Cliente atualizado no Asaas com sucesso!');
        } else {
          toast.success('Cliente criado no Asaas com sucesso!');
        }
        
        // Atualizar dados da pessoa
        const updatedPerson = {
          ...person,
          asaas_customer_id: syncResult.customer_id,
          asaas_sync_status: 'synced'
        };
        
        onPersonUpdate(updatedPerson);
      } else {
        toast.error(`Erro na sincronização: ${syncResult.error}`);
      }
    } catch (error) {
      console.error('Erro ao sincronizar com Asaas:', error);
      toast.error('Erro ao sincronizar com Asaas');
    } finally {
      setSyncingAsaas(false);
    }
  };  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[98vh] overflow-hidden flex flex-col">
        {/* Header com foto e informações principais */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Avatar/Foto - apenas para tipos que não são responsáveis */}
              {person.type !== 'parent' && (
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  {person.photo_url ? (
                    <img 
                      src={person.photo_url} 
                      alt={person.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-white" />
                  )}
                </div>
              )}
              
              {/* Nome e tipo */}
              <div>
                <h3 className="text-2xl font-bold">{person.name}</h3>
                <div className="flex items-center mt-1">
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    {(() => {
                      const TypeIcon = getPersonTypeIcon(person.type);
                      return <TypeIcon className="w-4 h-4 mr-1" />;
                    })()}
                    {getPersonTypeLabel(person.type)}
                  </span>
                  <span className={`ml-3 px-3 py-1 rounded-full text-sm font-medium ${
                    person.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {person.active ? '✓ Ativa' : '✗ Inativa'}
                  </span>
                </div>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>        {/* Conteúdo scrollável */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            
            {/* Coluna Esquerda */}
            <div className="space-y-4">
              
              {/* Informações de Contato */}
              <Card className="p-4">
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-blue-600" />
                  Contato
                </h4>
                <div className="space-y-3">                  {person.email && (
                    <div className="flex items-start">
                      <Mail className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500">Email</p>
                        <a href={`mailto:${person.email}`} className="text-blue-600 hover:text-blue-800 font-medium text-sm truncate block">
                          {person.email}
                        </a>
                      </div>
                    </div>
                  )}
                  {person.phone && (
                    <div className="flex items-start">
                      <Phone className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Telefone</p>
                        <a href={`tel:${person.phone}`} className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                          {formatPhone(person.phone)}
                        </a>
                      </div>
                    </div>
                  )}
                  {person.document && (
                    <div className="flex items-start">
                      <User className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">
                          {person.document.replace(/\D/g, '').length === 14 ? 'CNPJ' : 'CPF'}
                        </p>
                        <p className="font-medium text-gray-900 text-sm">{formatDocument(person.document)}</p>
                      </div>
                    </div>
                  )}
                  {person.birth_date && (
                    <div className="flex items-start">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Data de Nascimento</p>
                        <p className="font-medium text-gray-900 text-sm">
                          {new Date(person.birth_date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>              {/* Endereço */}
              {(person.address || person.city || person.state || person.zip_code || person.neighborhood) && (
                <Card className="p-4">
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-green-600" />
                    Endereço
                  </h4>
                  <div className="space-y-2">                    {person.address && (
                      <div>
                        <p className="text-xs text-gray-500">Logradouro</p>
                        <p className="font-medium text-gray-900 text-sm">
                          {person.address}
                          {person.address_number && `, ${person.address_number}`}
                          {person.address_complement && ` - ${person.address_complement}`}
                        </p>
                      </div>
                    )}
                    {person.neighborhood && (
                      <div>
                        <p className="text-xs text-gray-500">Bairro</p>
                        <p className="font-medium text-gray-900 text-sm">{person.neighborhood}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      {person.city && (
                        <div>
                          <p className="text-xs text-gray-500">Cidade</p>
                          <p className="font-medium text-gray-900 text-sm">{person.city}</p>
                        </div>
                      )}
                      {person.state && (
                        <div>
                          <p className="text-xs text-gray-500">Estado</p>
                          <p className="font-medium text-gray-900 text-sm">{person.state}</p>
                        </div>
                      )}
                    </div>
                    {person.zip_code && (
                      <div>
                        <p className="text-xs text-gray-500">CEP</p>
                        <p className="font-medium text-gray-900 text-sm">{person.zip_code}</p>
                      </div>
                    )}
                    {person.country && (
                      <div>
                        <p className="text-xs text-gray-500">País</p>
                        <p className="font-medium text-gray-900 text-sm">{person.country}</p>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>            {/* Coluna Direita */}
            <div className="space-y-4">
              
              {/* Informações específicas para Responsáveis e Funcionários (Asaas) */}
              {(person.type === 'parent' || person.type === 'employee') && (
                <Card className="p-4">
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <Building className="w-4 h-4 mr-2 text-purple-600" />
                    Integração Asaas
                  </h4>
                  <div className="space-y-3">                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500">Status da Sincronização</p>
                        <div className="flex items-center mt-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            person.asaas_sync_status === 'synced' 
                              ? 'bg-green-100 text-green-800' 
                              : person.asaas_sync_status === 'error'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {person.asaas_sync_status === 'synced' ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Sincronizado
                              </>
                            ) : person.asaas_sync_status === 'error' ? (
                              <>
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Erro
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Pendente
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      
                      <Button
                        onClick={handleSyncAsaas}
                        disabled={syncingAsaas}
                        variant="outline"
                        size="sm"
                        className="flex items-center text-xs"
                      >
                        {syncingAsaas ? (
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3 mr-1" />
                        )}
                        {syncingAsaas ? 'Sincronizando...' : 'Sincronizar'}
                      </Button>
                    </div>
                    
                    {person.asaas_customer_id && (
                      <div>
                        <p className="text-xs text-gray-500">ID no Asaas</p>
                        <p className="font-mono text-xs bg-gray-100 p-2 rounded">{person.asaas_customer_id}</p>
                      </div>
                    )}                    
                    {person.person_type && (
                      <div>
                        <p className="text-xs text-gray-500">Tipo de Pessoa</p>
                        <p className="font-medium text-gray-900 text-sm">
                          {person.person_type === 'FISICA' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                        </p>
                      </div>
                    )}
                    
                    {person.state_registration && (
                      <div>
                        <p className="text-xs text-gray-500">Inscrição Estadual</p>
                        <p className="font-medium text-gray-900 text-sm">{person.state_registration}</p>
                      </div>
                    )}
                    
                    {person.municipal_registration && (
                      <div>
                        <p className="text-xs text-gray-500">Inscrição Municipal</p>
                        <p className="font-medium text-gray-900 text-sm">{person.municipal_registration}</p>
                      </div>
                    )}
                    
                    {person.asaas_last_sync && (
                      <div>
                        <p className="text-xs text-gray-500">Última Sincronização</p>
                        <p className="text-xs text-gray-900">
                          {new Date(person.asaas_last_sync).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              )}              {/* Observações */}
              {(person.notes || person.observations) && (
                <Card className="p-4">
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <Settings className="w-4 h-4 mr-2 text-orange-600" />
                    Observações
                  </h4>
                  {person.notes && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Notas Gerais</p>
                      <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-2 rounded-lg text-sm">{person.notes}</p>
                    </div>
                  )}
                  {person.observations && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Observações do Cliente</p>
                      <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-2 rounded-lg text-sm">{person.observations}</p>
                    </div>
                  )}
                </Card>
              )}              {/* Informações do Sistema */}
              <Card className="p-4">
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-600" />
                  Informações do Sistema
                </h4>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">ID no Sistema</p>
                    <p className="font-mono text-xs bg-gray-100 p-2 rounded">{person.id}</p>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <p className="text-xs text-gray-500">Cadastrado em</p>
                      <p className="text-xs text-gray-900">
                        {person.created_at ? new Date(person.created_at).toLocaleString('pt-BR') : 'Não informado'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Última atualização</p>
                      <p className="text-xs text-gray-900">
                        {person.updated_at ? new Date(person.updated_at).toLocaleString('pt-BR') : 'Não informado'}
                      </p>
                    </div>
                  </div>
                  {person.auth_user_id && (
                    <div>
                      <p className="text-xs text-gray-500">Tem acesso ao sistema</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Key className="w-3 h-3 mr-1" />
                        Usuário autenticado
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>        {/* Footer com botões de ação */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to={`/dashboard/people/edit/${person.id}`}
              className="flex-1"
            >
              <Button variant="primary" className="w-full">
                <Edit className="w-4 h-4 mr-2" />
                Editar Pessoa
              </Button>
            </Link>            {/* Botão Histórico de Compras - apenas para tipos que fazem compras */}
            {(person.type === 'student' || person.type === 'customer' || person.type === 'employee') && (
              <Button 
                variant="outline" 
                onClick={handleViewPurchaseHistory}
                className="w-full sm:w-auto flex items-center justify-center"
                title={`Ver histórico de compras de ${person.name}`}
              >
                <Receipt className="w-4 h-4 mr-2" />
                Histórico de Compras
              </Button>
            )}
            
            <Button 
              variant="outline" 
              onClick={onClose}
              className="w-full sm:w-32"
            >
              <X className="w-4 h-4 mr-2" />
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonDetailsModal;